export type Service = {
  slug: string;
  title: string;
  eyebrow: string;
  summary: string;
  seoH1?: string;
  seoTitle?: string;
  seoDescription?: string;
  seoIntro?: string;
  bullets: string[];
  overview?: string;
  whatIs?: string;
  howItHelps?: { title: string; description: string }[];
  whatToExpect?: { title: string; description: string }[];
  idealFor?: string[];
  deliverables?: { title: string; description: string }[];
  process?: { step: string; title: string; description: string }[];
  outcomes?: { metric: string; label: string }[];
  faqs?: { q: string; a: string }[];
};

export const services: Service[] = [
  {
    slug: "seo",
    title: "Semantic SEO & AEO",
    eyebrow: "01 · Search",
    seoH1: "SEO content strategy & semantic SEO services",
    seoTitle: "SEO Content Strategy Services | MG Content Labs",
    seoDescription:
      "Semantic SEO and AEO services: topic clusters, entity mapping, technical SEO, and an SEO content strategy built to rank in Google and get cited by AI search.",
    seoIntro:
      "Our SEO content strategy service combines semantic SEO, topical authority mapping, and answer engine optimization, so your pages rank in Google search results and get cited inside AI answers from ChatGPT, Gemini, and Perplexity.",
    summary:
      "Beyond keywords. We map your content to topic clusters and entities that both search engines and large language models cite as authoritative sources.",
    bullets: [
      "Answer Engine Optimization (ChatGPT, Gemini, Perplexity)",
      "Topical maps & entity coverage",
      "Technical SEO audit & Core Web Vitals",
      "Programmatic SEO for scale",
    ],
    overview:
      "Modern search is a two-front war. Google's AI Overviews and generative engines like ChatGPT, Gemini, and Perplexity now synthesize answers directly — often without a click. We architect your site so it wins both: ranked in the classic SERP and cited as the source inside AI answers.",
    whatIs:
      "Semantic SEO & Answer Engine Optimization goes beyond basic keywords. We develop comprehensive topic clusters and entity relationships so your content is recognized as an authoritative source by both traditional search engines and AI platforms like ChatGPT, Gemini, and Perplexity.",
    howItHelps: [
      { title: "Why it matters", description: "Search intent is increasingly conversational and AI-driven. Content optimized only for classic SEO often fails to appear in AI-generated answers, limiting your reach as more users rely on generative tools." },
      { title: "How you benefit", description: "Your content ranks higher in Google, gets frequently cited by AI engines, builds long-term topical authority, and drives consistent organic growth with reduced dependency on single keywords." },
      { title: "Visibility across search and AI", description: "Structured, entity-rich content wins traditional rankings and gets cited as a source inside ChatGPT, Gemini, and Perplexity answers." },
      { title: "Compounding organic growth", description: "Topic clusters and entity coverage create a defensible authority moat that keeps producing traffic long after the initial work ships." },
    ],
    whatToExpect: [
      { title: "Weeks 1–4 · Discovery", description: "Technical audit, entity mapping, and a prioritised list of the highest-leverage topics and pages." },
      { title: "Months 2–3 · Production", description: "Topic clusters, rewrites, and new pages ship with schema, internal linking, and answer-engine formatting." },
      { title: "Months 3–6 · Ranking & citation growth", description: "Long-tail queries land first, followed by AI platform citations and mid-funnel ranking gains." },
      { title: "Months 6+ · Compounding", description: "Traffic, rankings, and AI citations stack month over month as authority compounds." },
    ],
    idealFor: [
      "B2B and SaaS brands with complex buying journeys",
      "Companies that have plateaued with keyword-only SEO",
      "Teams preparing for the shift from Google clicks to AI answers",
    ],
    deliverables: [
      { title: "Answer Engine Optimization (AEO)", description: "Tailored for major AI platforms including ChatGPT, Gemini, and Perplexity." },
      { title: "Topical maps and entity coverage", description: "Comprehensive maps that connect every relevant topic and entity your brand should own." },
      { title: "Technical SEO audits", description: "Deep audits with Core Web Vitals optimization and prioritised fix recommendations." },
      { title: "Programmatic SEO", description: "Scalable content production systems for long-tail and comparison coverage." },
    ],
    process: [
      { step: "01", title: "Discovery", description: "Technical audit, competitive teardown, and entity mapping." },
      { step: "02", title: "Architecture", description: "Build topical maps and prioritise page-level opportunities." },
      { step: "03", title: "Production", description: "Ship optimised content with schema, internal links, and AEO formatting." },
      { step: "04", title: "Measurement", description: "Track rankings, AI citations, and organic pipeline contribution." },
    ],
    outcomes: [
      { metric: "30-100%+", label: "organic traffic growth within 6-18 months" },
      { metric: "Higher", label: "Google rankings and AI engine citations" },
      { metric: "Consistent", label: "qualified organic traffic growth" },
    ],
    faqs: [
      { q: "How is this different from traditional SEO?", a: "Traditional SEO optimises for keywords and pages. Semantic SEO optimises for topics, entities, and intent across clusters — and AEO ensures your content is structured for AI-generated answers." },
      { q: "When will I see results?", a: "Ranking movement typically appears within 3–6 months. AI citations can appear faster because they favour well-structured, authoritative content." },
    ],
  },
  {
    slug: "content",
    title: "Content Strategy & Editorial",
    eyebrow: "02 · Editorial",
    seoH1: "Content writing services & editorial strategy",
    seoTitle: "Content Writing Services in India | MG Content Labs",
    seoDescription:
      "Content writing services and editorial strategy for B2B and SaaS brands: blog clusters, whitepapers, and long-form content written by senior specialists.",
    seoIntro:
      "Our content writing services cover blog content, long-form whitepapers, and technical documentation, backed by a documented content strategy that maps every asset to a buyer stage and a search intent.",
    summary:
      "Thought leadership that converts. We produce long-form whitepapers, blog clusters, and technical documentation your buyers actually finish.",
    bullets: [
      "Editorial calendar & narrative strategy",
      "SME interviews and ghostwriting",
      "Long-form articles (2,000–6,000 words)",
      "Whitepapers, ebooks, and reports",
    ],
    overview:
      "Great B2B content is not a blog — it's a point of view. We help you find the one narrative your market cannot get anywhere else, then ship it as a coordinated system of articles, whitepapers, and executive posts that compound.",
    whatIs:
      "Content strategy is the operating system behind everything you publish: who you're talking to, what point of view only you can hold, which formats carry it, and how each asset ladders up to pipeline. Editorial is the craft that ships it — long-form articles, whitepapers, and executive thought leadership your buyers actually finish.",
    howItHelps: [
      { title: "A narrative your market can't get elsewhere", description: "We find the wedge — the specific take your competitors can't or won't make — and build every asset around it." },
      { title: "Sales enablement that isn't a deck", description: "The right long-form asset shortens sales cycles because prospects arrive to calls already convinced." },
      { title: "Executive presence at scale", description: "Ghostwritten thought leadership makes your founders visible where your buyers actually spend attention." },
      { title: "A reusable asset library", description: "Every piece is designed to be atomised into social, email, and sales collateral — not published and forgotten." },
    ],
    whatToExpect: [
      { title: "Weeks 1–2 · Positioning & voice", description: "SME interviews and competitor teardown produce your voice guide and narrative wedge." },
      { title: "Weeks 3–4 · Editorial calendar", description: "A 90-day calendar mapped to funnel stage, ICPs, and business outcomes." },
      { title: "Month 2+ · Weekly ship cadence", description: "Long-form articles, exec LinkedIn posts, and quarterly flagship assets ship on a predictable rhythm." },
      { title: "Quarterly · Review & pivot", description: "We track read-through, MQL sourcing, and pipeline influence — and rebalance the calendar accordingly." },
    ],
    idealFor: [
      "Founder-led B2B and SaaS companies with a strong POV",
      "Marketing teams shipping content but missing an editorial spine",
      "Brands that want thought leadership without hiring a full editorial team",
    ],
    deliverables: [
      { title: "Narrative & Voice Guide", description: "Your positioning distilled into an editorial voice: what you say, what you refuse to say, and how it sounds on the page." },
      { title: "Rolling Editorial Calendar", description: "A 90-day calendar mapped to funnel stage, ICP segments, and business outcomes — reviewed monthly with your team." },
      { title: "SME Ghostwriting", description: "We interview your founders and experts, then ship LinkedIn posts and long-form articles under their byline." },
      { title: "Flagship Assets", description: "Annual reports, industry benchmarks, and category-defining whitepapers with original research and design." },
    ],
    process: [
      { step: "01", title: "Positioning Audit", description: "We map your competitors' narratives and find the gap only you can own." },
      { step: "02", title: "Editorial Playbook", description: "Voice, formats, and workflow codified into a single reference doc." },
      { step: "03", title: "Production Cadence", description: "Weekly editorial standup with our editor embedded in your Slack." },
      { step: "04", title: "Distribution", description: "Every asset ships with a launch plan across social, email, and community." },
    ],
    outcomes: [
      { metric: "6×", label: "avg. read-through vs. industry benchmark" },
      { metric: "42%", label: "of MQLs cite an article at first touch" },
      { metric: "12", label: "flagship assets shipped per year on Growth plan" },
    ],
    faqs: [
      { q: "Do you write in our voice?", a: "Yes. We spend the first two weeks on a voice guide and every writer trains against it. Founders typically approve pieces in one revision by month two." },
      { q: "Who owns the copyright?", a: "You do. Every deliverable is a work-for-hire assigned to your company on publication." },
    ],
  },
  {
    slug: "smo",
    title: "Social Media Optimization",
    eyebrow: "03 · Distribution",
    seoH1: "Social media optimization services",
    seoTitle: "Social Media Optimization Services (SMO) | MG Content Labs",
    seoDescription:
      "Social media optimization services that turn long-form content into LinkedIn, X, and newsletter distribution — profile optimization, repurposing, and reporting.",
    seoIntro:
      "Our social media optimization services (SMO) turn every long-form asset into a distribution system: optimized profiles, repurposed formats for LinkedIn and X, newsletter syndication, and reporting that ties social reach back to pipeline.",
    summary:
      "Amplifying your voice where it matters. Multi-channel distribution across LinkedIn, X, and industry verticals — atomized from your core narrative.",
    bullets: [
      "LinkedIn executive presence",
      "Narrative atomization",
      "Community & newsletter growth",
      "Content-to-social pipelines",
    ],
    overview:
      "Publishing an article is 20% of the work. The other 80% is distribution. We turn every long-form asset into a coordinated wave of LinkedIn posts, X threads, newsletter drops, and community activations — so nothing you publish quietly disappears.",
    whatIs:
      "Social Media Optimization (SMO) is the discipline of engineering your content so it travels — inside LinkedIn feeds, X threads, industry newsletters, and private communities where your buyers actually spend attention. It's distribution as a system, not as an afterthought.",
    howItHelps: [
      { title: "10× the reach of your existing content", description: "The same article, atomised correctly, gets 8–12 native social pieces — each one another surface area for discovery." },
      { title: "Executive influence that compounds", description: "A founder posting consistently for 12 months becomes a category voice — and category voices attract inbound." },
      { title: "Direct pipeline from social DMs", description: "LinkedIn is now a top-3 sourced channel for B2B pipeline when the exec posts with real substance." },
      { title: "Community-led credibility", description: "Being seeded in the right Slack groups, subreddits, and newsletters is worth more than a boosted post." },
    ],
    whatToExpect: [
      { title: "Weeks 1–2 · Voice capture", description: "We interview your exec, study their existing writing, and build a voice bank of hooks, phrases, and taboos." },
      { title: "Weeks 3–4 · First posts live", description: "Weekly editorial batch shipped, approved in one Loom, scheduled across channels." },
      { title: "Months 2–3 · Early traction", description: "Impressions typically double, follower quality improves, first inbound DMs land." },
      { title: "Month 4+ · Category voice", description: "Reporting shifts to inbound conversations, pipeline sourced, and community mentions." },
    ],
    idealFor: [
      "Founders and executives who want to build presence but don't have time to write",
      "Companies with strong long-form content but weak distribution",
      "Brands entering a new category and needing to be seen fast",
    ],
    deliverables: [
      { title: "Executive LinkedIn Programme", description: "3–5 posts a week for your founder or exec, written in their voice with performance-tested hooks." },
      { title: "Narrative Atomization", description: "Every long-form piece is repurposed into 8–12 native social assets across formats." },
      { title: "Community & Newsletter", description: "We seed content into the Slack groups, subreddits, and newsletters your buyers actually read." },
      { title: "Analytics & Iteration", description: "Weekly reporting on impressions, follower quality, inbound DMs, and pipeline attribution." },
    ],
    process: [
      { step: "01", title: "Audience Mapping", description: "Where does your ICP actually spend attention? We map channels by leverage, not vanity." },
      { step: "02", title: "Voice Calibration", description: "Two-week onboarding to capture your exec's voice — the phrases, the takes, the taboos." },
      { step: "03", title: "Publish Rhythm", description: "A weekly editorial batch, approved in one Loom, scheduled across channels." },
      { step: "04", title: "Amplify", description: "Employee advocacy, community seeding, and paid boost on top-quartile posts." },
    ],
    outcomes: [
      { metric: "+180%", label: "avg. LinkedIn impressions in 90 days" },
      { metric: "22×", label: "inbound DMs sourced per quarter" },
      { metric: "4×", label: "engagement rate vs. category benchmark" },
    ],
    faqs: [
      { q: "Will you post from my personal account?", a: "Yes — with your final approval on every post. Most execs move to same-day auto-publish by month three." },
      { q: "Do you do paid social?", a: "We run tactical boosts on top-performing organic posts. Full paid media programmes are handled by our partner agencies." },
    ],
  },
  {
    slug: "optimization",
    title: "Content Optimization",
    eyebrow: "04 · Growth",
    seoH1: "Content optimization services",
    seoTitle: "Content Optimization Services | MG Content Labs",
    seoDescription:
      "Content optimization services for existing pages: content audits, rewrites, internal linking, and on-page SEO that recover rankings and lift conversion rates.",
    seoIntro:
      "Our content optimization services focus on the pages you already have. We audit existing content, refresh decaying posts, fix on-page SEO and internal linking, and rewrite underperforming pages so your current library earns more traffic and more conversions.",
    summary:
      "Historical content refresh to revive decaying traffic and maximize ROI from your existing asset library. Big lifts, small investments.",
    bullets: [
      "Content refresh & re-optimization",
      "Internal linking architecture",
      "Conversion rate optimization",
      "SERP feature capture",
    ],
    overview:
      "Your best growth lever is often the content you already published. We audit your library, identify pages one push away from page one, and rewrite them with modern intent, entity coverage, and conversion paths — usually doubling their traffic in a quarter.",
    whatIs:
      "Content Optimization is the disciplined refresh of your existing content library — auditing every published URL, identifying pages that decayed or never reached potential, and rewriting them with updated intent, entities, structure, and conversion paths. It's the fastest, highest-ROI SEO lever most companies never pull.",
    howItHelps: [
      { title: "Fastest ROI in the whole SEO stack", description: "Refreshed pages typically move within 4–8 weeks — much faster than net-new content, because they already have authority signals." },
      { title: "Recovers decayed traffic", description: "Every content library loses 20–40% of its traffic per year to freshness decay. We reverse it." },
      { title: "Unlocks page-2 wins", description: "Pages ranking 11–20 are one strong rewrite from page one — where nearly all clicks happen." },
      { title: "Improves conversion, not just traffic", description: "We rebuild in-article CTAs, lead magnets, and internal links so refreshed traffic converts, not just visits." },
    ],
    whatToExpect: [
      { title: "Week 1 · Full library audit", description: "Every indexed URL scored on traffic, ranking proximity, conversion, and refresh effort." },
      { title: "Week 2 · Prioritised roadmap", description: "The 30–50 highest-ROI candidates for the next quarter, mapped to a Consolidate / Deepen / Expand / Pivot pattern." },
      { title: "Weeks 3+ · Monthly refresh sprints", description: "8–15 rewrites shipped per month, published directly to your CMS." },
      { title: "Weeks 8–12 · Measurable uplift", description: "Before/after reporting shows median 2×+ traffic lift on refreshed URLs." },
    ],
    idealFor: [
      "Companies with 50+ published articles and flat or declining organic traffic",
      "Teams that invested in content in 2020–2023 and haven't touched it since",
      "Brands wanting SEO results this quarter, not next year",
    ],
    deliverables: [
      { title: "Content Audit Scorecard", description: "Every URL scored on traffic, ranking proximity, conversion, and refresh effort — ranked by leverage." },
      { title: "Refresh Sprints", description: "Monthly batches of 8–15 rewrites using our four proven patterns: Consolidate, Deepen, Expand, Pivot." },
      { title: "Internal Linking Rebuild", description: "A programmatic linking layer that funnels authority to your money pages and topic hubs." },
      { title: "CRO on Long-Form", description: "In-article CTAs, exit-intent, and lead magnets built for the buying journey, not the traffic count." },
    ],
    process: [
      { step: "01", title: "Library Audit", description: "Full inventory of your indexed URLs with a leverage score for each." },
      { step: "02", title: "Prioritisation", description: "We select the 30–50 highest-ROI candidates for the next quarter." },
      { step: "03", title: "Rewrite & Ship", description: "Editors execute using the pattern best matched to each page's decay reason." },
      { step: "04", title: "Track Uplift", description: "Before/after reporting on rankings, traffic, and conversions per refreshed URL." },
    ],
    outcomes: [
      { metric: "2.1×", label: "median traffic lift on refreshed URLs" },
      { metric: "78%", label: "of page-2 targets reaching page 1" },
      { metric: "9 wks", label: "avg. time to measurable uplift" },
    ],
    faqs: [
      { q: "Do you touch our CMS directly?", a: "Yes. We work in WordPress, Webflow, HubSpot, Contentful, Sanity, and custom stacks — or hand off edit-ready docs if you prefer." },
      { q: "What if the page is already ranking #1?", a: "We leave it alone. Refresh is about reclaiming decay, not disturbing winners." },
    ],
  },
  {
    slug: "linkbuilding",
    title: "Digital PR & Link Building",
    eyebrow: "05 · Authority",
    seoH1: "Link building services in India",
    seoTitle: "Link Building Services in India | MG Content Labs",
    seoDescription:
      "Digital PR and link building services in India: editorial backlinks, data-led campaigns, and expert commentary that build domain authority without link schemes.",
    seoIntro:
      "Our link building services in India earn editorial backlinks through digital PR — original data studies, expert commentary, and outreach to publications your buyers already read. No link farms, no paid link schemes.",
    summary:
      "Earned links from tier-one publications. We build durable domain authority through original research, data journalism, and expert outreach.",
    bullets: [
      "Data-driven digital PR campaigns",
      "HARO/qwoted expert placements",
      "Guest posting on DR 60+ sites",
      "Broken link reclamation",
    ],
    overview:
      "The link building playbook that worked in 2019 is dead. We build authority the way modern journalists reward it: with original data, credible experts, and stories worth citing. Every link is earned, on-topic, and defensible.",
    whatIs:
      "Link building is the practice of earning citations from other credible websites — because in Google's eyes, links are still the strongest signal of authority. Modern link building is Digital PR: you don't beg for links, you produce data, stories, and expert commentary that journalists genuinely want to cite.",
    howItHelps: [
      { title: "Unlocks rankings you can't reach on content alone", description: "In competitive categories, on-page work gets you to page 2. Authority is what gets you into the top 5." },
      { title: "Compounds domain-wide", description: "A single tier-one link lifts the whole domain — every future page you publish inherits that authority." },
      { title: "Brand credibility, not just SEO", description: "Being quoted in Forbes, TechCrunch, or your industry trade press earns trust no ad ever will." },
      { title: "Referral traffic and pipeline", description: "Well-placed links from the right publications send qualified readers directly to your product." },
    ],
    whatToExpect: [
      { title: "Weeks 1–3 · Angle & asset", description: "We develop story angles reporters actually want, then produce the survey, dataset, or report to back them." },
      { title: "Weeks 4–6 · Outreach", description: "Warm, personalised pitches to a curated list of journalists on your beat." },
      { title: "Weeks 6–12 · Placements land", description: "Tier-one press coverage begins to roll in, tracked with DR, traffic, and topical relevance." },
      { title: "Quarterly · New flagship study", description: "One original research campaign per quarter keeps the pipeline of earned links refilling." },
    ],
    idealFor: [
      "Brands competing in high-difficulty categories where on-page SEO alone won't rank",
      "Companies with newsworthy data or a founder credible enough to be quoted",
      "Teams tired of buying spammy links that never move the needle",
    ],
    deliverables: [
      { title: "Data Journalism Campaigns", description: "One flagship study per quarter — surveyed, analysed, designed, and pitched to tier-one press." },
      { title: "Expert Commentary Programme", description: "We place your executives as quoted sources in journalist requests across HARO, Qwoted, and Featured." },
      { title: "Curated Guest Posting", description: "Bylined placements on DR 60+ publications that your ICP actually reads — never PBNs, never spam." },
      { title: "Broken Link Reclamation", description: "We recover lost equity from unlinked brand mentions and dead resource pages linking to competitors." },
    ],
    process: [
      { step: "01", title: "Angle Development", description: "Story angles that reporters want to cover — not press releases." },
      { step: "02", title: "Research & Assets", description: "Original surveys, data pulls, and designed reports built for citation." },
      { step: "03", title: "Outreach", description: "Warm, personalised pitches to a curated list of journalists on your beat." },
      { step: "04", title: "Report & Repeat", description: "Monthly link report with DR, traffic, and topical relevance for every earned placement." },
    ],
    outcomes: [
      { metric: "18+", label: "tier-1 placements per year on Growth plan" },
      { metric: "DR 74", label: "avg. authority of earned domains" },
      { metric: "0", label: "spammy, PBN, or paid link scheme placements — ever" },
    ],
    faqs: [
      { q: "Do you buy links?", a: "No. Every link is editorially earned. We don't touch PBNs, link exchanges, or paid schemes — they get you penalised." },
      { q: "Can you guarantee a specific number of links?", a: "We forecast a range based on your niche and asset quality, but we won't guarantee a number to a Google-quality bar. Anyone who does is buying links." },
    ],
  },
  {
    slug: "aeo",
    title: "AEO & Generative Engine Optimization",
    eyebrow: "06 · AI",
    seoH1: "Answer engine optimization services",
    seoTitle: "Answer Engine Optimization (AEO) Services | MG Content Labs",
    seoDescription:
      "Answer engine optimization services that make your brand the cited source inside ChatGPT, Gemini, Perplexity, and Google AI Overviews.",
    seoIntro:
      "Answer engine optimization (AEO), also called generative engine optimization (GEO), structures your content so AI search engines quote it. We handle entity coverage, schema markup, answer-first formatting, and citation tracking across ChatGPT, Gemini, Perplexity, and Google AI Overviews.",
    summary:
      "Be the answer, not just a result. We optimize your content for AI Overviews, ChatGPT citations, and Perplexity sources.",
    bullets: [
      "LLM-ready content structuring",
      "Schema markup & structured data",
      "Citation-worthy statistics & studies",
      "Prompt-visible brand positioning",
    ],
    overview:
      "In the AI era, half your buyers ask a language model before they ever open a browser. We engineer your content so ChatGPT, Gemini, Perplexity, and Google's AI Overviews quote you as the definitive source — with your brand name intact.",
    whatIs:
      "AEO (Answer Engine Optimization) — sometimes called GEO (Generative Engine Optimization) — is the discipline of getting your brand cited inside AI-generated answers. When a buyer asks ChatGPT, Gemini, Perplexity, or Google's AI Overview a question in your category, AEO determines whether your brand shows up in the answer or gets skipped entirely.",
    howItHelps: [
      { title: "Visibility in the new zero-click era", description: "AI answers already handle 20–40% of buyer research. If you're not in the answer, you don't exist to that buyer." },
      { title: "Higher-intent traffic", description: "Users who click through from an AI citation arrive with the question already framed — they convert 2–3× better than cold search traffic." },
      { title: "Brand mention share, not just links", description: "Being named in an AI answer builds category authority even when the user never visits your site." },
      { title: "First-mover advantage", description: "AI ranking signals are still forming. Brands who optimise now will be the defaults when the space matures." },
    ],
    whatToExpect: [
      { title: "Weeks 1–3 · Prompt baseline", description: "We test 200+ buyer prompts across ChatGPT, Gemini, Perplexity, and Claude and score your current share of voice." },
      { title: "Weeks 4–8 · Gap fixing", description: "For every prompt you lose, we identify the missing entity, fact, or structure and ship the fix." },
      { title: "Months 2–4 · Citation lift", description: "Mention rate typically grows 3–5× as restructured content is picked up by LLM indexes." },
      { title: "Ongoing · Monthly LLM report", description: "Prompt-level wins, losses, and next moves — the only reporting layer that actually reflects the new search reality." },
    ],
    idealFor: [
      "SaaS, B2B, and consumer brands where buyers research with ChatGPT before Google",
      "Companies with strong SEO who are watching organic clicks decline as AI answers grow",
      "Founders who want to be the category default inside language models",
    ],
    deliverables: [
      { title: "Prompt Visibility Audit", description: "We test 200+ buyer prompts across the top LLMs and score your current share of voice against competitors." },
      { title: "LLM-Ready Restructuring", description: "Rewrites that use definitional openers, comparison tables, and passage-level structure so language models can extract you cleanly." },
      { title: "Schema & Entity Layer", description: "Article, Organization, Product, and FAQ schema wired into every page — plus knowledge-graph reconciliation." },
      { title: "Citation-Grade Research", description: "Original data points and statistics designed to be the number LLMs quote when someone asks your category question." },
    ],
    process: [
      { step: "01", title: "Baseline Prompts", description: "We build a scored prompt set that mirrors your real buyer journey." },
      { step: "02", title: "Content Gaps", description: "For every prompt you lose, we identify the missing entity or fact." },
      { step: "03", title: "Restructure & Ship", description: "Content is rewritten and re-published with LLM-friendly structure." },
      { step: "04", title: "Monitor Share of Voice", description: "Monthly LLM visibility report with prompt-level wins, losses, and next moves." },
    ],
    outcomes: [
      { metric: "5×", label: "AI citation share within 6 months" },
      { metric: "Top 3", label: "brand mentioned in the answer, not just linked" },
      { metric: "200+", label: "prompts monitored monthly across LLMs" },
    ],
    faqs: [
      { q: "Can you actually track LLM citations?", a: "Yes. We run a monitored prompt set weekly against ChatGPT, Gemini, Perplexity, and Claude and report your brand's mention rate, position, and sentiment." },
      { q: "Is AEO different from SEO?", a: "It shares a foundation with SEO but the ranking signals — structure, factual density, entity clarity — are weighted very differently. We run them as one coordinated programme." },
    ],
  },
];

export type Plan = {
  slug: string;
  name: string;
  price: { usd: string; inr: string };
  cadence: string;
  tagline: string;
  features: string[];
  featured?: boolean;
  cta: string;
  ctaKind: "checkout" | "contact";
};

export const plans: Plan[] = [
  {
    slug: "baseline",
    name: "Baseline",
    price: { usd: "$119", inr: "₹9,990" },
    cadence: "/month",
    tagline: "For teams starting their content engine.",
    features: [
      "4 authoritative long-form articles",
      "Keyword & topic mapping",
      "Monthly performance report",
      "Social snippet distribution pack",
      "On-page SEO optimization",
    ],
    cta: "Start with Baseline",
    ctaKind: "checkout",
  },
  {
    slug: "growth",
    name: "Growth",
    price: { usd: "$215", inr: "₹17,990" },
    cadence: "/month",
    tagline: "Our most-loved plan. Built for compounding results.",
    features: [
      "8 authoritative long-form articles",
      "Full semantic SEO strategy",
      "AEO (AI Engine) optimization",
      "Multi-channel SMO distribution",
      "Monthly strategy call with your editor",
      "Content refresh & optimization",
    ],
    featured: true,
    cta: "Start with Growth",
    ctaKind: "checkout",
  },
  {
    slug: "enterprise",
    name: "Enterprise",
    price: { usd: "Custom", inr: "Custom" },
    cadence: "",
    tagline: "Full editorial department, embedded.",
    features: [
      "Dedicated content editor",
      "Thought-leadership ghostwriting",
      "Proprietary topic clustering",
      "Full technical SEO audit",
      "Digital PR & link building",
      "Custom research & data studies",
    ],
    cta: "Contact Sales",
    ctaKind: "contact",
  },
];
